import React, { useState } from 'react';
import { useQuery, useApolloClient } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import classnames from 'classnames';
import { toast } from 'react-toastify';

import { openAuthURL } from 'helpers';
import { getIntegrationToken, generateIntegrationOAuthUrl } from 'helpers/integrations';
import { Button, Modal, TokenIntegrationModalContent, Loader, Toast } from 'components';
import { refreshIntegrationsQuery } from 'services/apollo/queries';
import { ManageAppsIntegrationImage } from 'screens/ManageAppScreen';
import { Logger } from 'services';
import { useTranslation } from 'hooks';

import styles from './ConfigureIntegrations.module.scss';

const ConfigureIntegrationsQuery = gql`
  query getIntegrations {
    me {
      userProfile {
        id
      }
      organization {
        id
      }
    }
    integrations {
      type
      title
      setupPath
      image
      imageIcon
      imageProviderIcon
      userConfig {
        instructions
        fields {
          name
          label
        }
      }
      features {
        configurable
      }
    }
  }
`;

export const ConfigureIntegrations = () => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const [modalIsOpen, setIsOpen] = useState(false);
  const [integrationToConfigure, setIntegrationToConfigure] = useState(null);
  const openModal = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };
  const { error, data } = useQuery(ConfigureIntegrationsQuery, {
    fetchPolicy: 'cache-and-network',
  });

  if (data === undefined) return <Loader />;

  if (error) return <Loader color="Red" />;

  const { me, integrations } = data;

  return (
    <div className={styles.buttonGrid}>
      {integrations.map((integration: any) => {
        const id = integration.type!.slice(0, integration.type!.indexOf('@'));

        // If token integration show modal, otherwise generate OAuth url
        const configureIntegrations = () => {
          if (integration.userConfig) {
            setIntegrationToConfigure(integration);
            openModal();
          } else {
            getIntegrationToken().then((token) => {
              const url = generateIntegrationOAuthUrl(
                token,
                integration.setupPath,
                id,
                me.userProfile.id,
                me.organization.id
              );
              openAuthURL(
                url,
                true,
                '_blank',
                () => {
                  client
                    .query({
                      query: refreshIntegrationsQuery,
                      fetchPolicy: 'network-only',
                    })
                    .catch((error) => {
                      Logger.error(error);
                    });
                },
                () => {
                  toast(<Toast message={t('toasts.pop-up-blocked')} hasCloseButton />, {
                    position: toast.POSITION.BOTTOM_LEFT,
                    hideProgressBar: true,
                    closeButton: false,
                    closeOnClick: false,
                    autoClose: false,
                  });
                }
              );
            });
          }
        };

        return (
          <Button
            className={classnames(styles.button, {
              [styles[`${id}`]]: id,
            })}
            data-integration={id}
            key={`${id}`}
            type="Integration"
            onClick={configureIntegrations}
          >
            <ManageAppsIntegrationImage itemIntegrationName={id} />
          </Button>
        );
      })}
      <Modal isOpen={modalIsOpen} onRequestClose={closeModal}>
        <TokenIntegrationModalContent
          integration={integrationToConfigure}
          closeModal={closeModal}
        />
      </Modal>
    </div>
  );
};

export default ConfigureIntegrations;

/* TODO - #32 Analytics
  this.props.tracking.trackEvent({
    event: eventTrackingNames.manageApps.addAppSuccessful,
    metadata: {
      [eventTrackingProperties.metadata.appType]:
        prevProps.integrationTypeToBeAdded
    }
  });
*/
